import { useState } from 'react';

import { ErroDeRede, lerLaudo } from '../nucleo/api.js';
import { posicaoNaEscalaEmoc } from '../nucleo/laboratorio.js';
import { enxuto, lerNumero, porcento } from '../nucleo/numero.js';

/**
 * "O que este laudo está dizendo?" — ler DQO, COT e DBO como uma frase.
 *
 * A razão DQO/COT é uma identidade estequiométrica, não uma correlação: ela
 * dá o estado médio de oxidação do carbono, e dele sai direto a fração
 * teórica de CH₄ no biogás. Isso é raro e vale dizer — quase tudo nesta
 * plataforma é empírico com erro declarado, e este pedaço é álgebra.
 *
 * O que NÃO sai daí é se o material degrada. Esse é o outro eixo, e vem da
 * DBO. Os dois são independentes, e a confusão entre eles é o erro clássico
 * de leitura de laudo: um material pode ter carbono muito reduzido (teto de
 * metano alto) e ser completamente recalcitrante — lignina é exatamente isso.
 * Por isso a tela desenha DOIS eixos e nunca um só.
 */
const CAMPOS = [
  {
    campo: 'dqo',
    rotulo: 'DQO',
    unidade: 'mg/L ou g/kg',
    dica: 'Demanda química de oxigênio.',
  },
  {
    campo: 'cot',
    rotulo: 'COT — carbono ORGÂNICO total',
    unidade: 'mesma unidade da DQO',
    dica:
      'Orgânico, não total. Num digestato tamponado o carbono inorgânico (bicarbonato) ' +
      'pode ser metade do carbono do frasco, e usá-lo aqui derruba a razão e inventa um ' +
      'carbono oxidado que não existe.',
  },
  {
    campo: 'dbo',
    rotulo: 'DBO',
    unidade: 'mesma unidade da DQO',
    dica: 'Sem ela sai a escala de oxidação, mas não sai veredito de biodegradabilidade.',
  },
  {
    campo: 'ch4_medido_pct',
    rotulo: 'CH₄ medido no biogás',
    unidade: '%',
    dica: 'Se o ensaio já rodou, o gás medido é confrontado com a previsão teórica.',
  },
];

export function LaboratorioLaudo() {
  const [valores, setValores] = useState({});
  const [rotulo, setRotulo] = useState('');
  const [resposta, setResposta] = useState(null);
  const [erro, setErro] = useState(null);
  const [lendo, setLendo] = useState(false);

  function enviar(evento) {
    evento.preventDefault();
    setLendo(true);
    setErro(null);

    const corpo = {};
    for (const [campo, texto] of Object.entries(valores)) {
      const n = lerNumero(texto);
      if (n !== null) corpo[campo] = n;
    }
    if (rotulo.trim()) corpo.rotulo_do_laboratorio = rotulo.trim();

    lerLaudo(corpo)
      .then((r) => setResposta(r))
      .catch((e) => setErro(e))
      .finally(() => setLendo(false));
  }

  return (
    <div className="empilha-largo">
      <section className="bloco">
        <h2 className="simulador__titulo">O que este laudo está dizendo?</h2>
        <p className="simulador__chamada">
          DQO e COT na mesma unidade — a razão entre elas é adimensional e é daí que sai
          tudo. A DBO é opcional e é ela que acrescenta o segundo eixo.
        </p>
      </section>

      <form className="bloco" onSubmit={enviar}>
        <div className="grupo-de-analise__campos">
          {CAMPOS.map((c) => (
            <div className="campo" key={c.campo}>
              <label className="campo__rotulo" htmlFor={c.campo}>
                {c.rotulo} <span className="campo__opcional">{c.unidade}</span>
              </label>
              <input
                id={c.campo}
                className="campo__entrada"
                inputMode="decimal"
                value={valores[c.campo] ?? ''}
                onChange={(e) => setValores((v) => ({ ...v, [c.campo]: e.target.value }))}
              />
              <p className="campo__dica">{c.dica}</p>
            </div>
          ))}
          <div className="campo">
            <label className="campo__rotulo" htmlFor="rotulo">
              O que o laudo chamou este material{' '}
              <span className="campo__opcional">opcional</span>
            </label>
            <input
              id="rotulo"
              className="campo__entrada"
              value={rotulo}
              onChange={(e) => setRotulo(e.target.value)}
            />
            <p className="campo__dica">
              Serve para ser confrontado: se o papel diz "celulose" e a razão DQO/COT diz
              outra coisa, quem está errado é uma das duas, e vale saber.
            </p>
          </div>
        </div>

        <button type="submit" className="botao" disabled={lendo}>
          {lendo ? 'Lendo…' : 'Ler o laudo'}
        </button>
      </form>

      {erro ? (
        <section className="bloco" role="alert">
          <h3 className="rotulo-secao">A leitura não saiu</h3>
          <p className="campo__dica campo__dica--erro">
            {erro instanceof ErroDeRede
              ? 'Não consegui falar com o servidor. Tente de novo quando a conexão voltar.'
              : erro.message}
          </p>
        </section>
      ) : null}

      {resposta ? <Leitura resposta={resposta} /> : null}
    </div>
  );
}

function Leitura({ resposta }) {
  const posicao = posicaoNaEscalaEmoc(resposta.emoc);

  return (
    <>
      <section className="bloco">
        <h3 className="rotulo-secao">Onde este carbono está</h3>
        <p className="ficha__linha">
          <span className="numero-grande">{enxuto(resposta.dqo_cot, 2)}</span>{' '}
          <span className="unidade">de DQO/COT</span>
        </p>

        <div className="regua-emoc">
          <div className="regua-emoc__trilho">
            <div className="regua-emoc__marca" style={{ left: `${posicao * 100}%` }} />
          </div>
          <p className="regua-emoc__pontas">
            <span>+4 · CO₂, carbono já oxidado</span>
            <span>−4 · CH₄, carbono totalmente reduzido</span>
          </p>
        </div>

        <p className="terciario">
          Estado médio de oxidação {enxuto(resposta.emoc, 2)}. Quanto mais à direita, mais
          elétrons o carbono ainda tem para dar — e é isso, não a biologia, que fixa o teto
          de metano.
        </p>
      </section>

      <section className="bloco">
        <h3 className="rotulo-secao">O que isso implica</h3>
        <p className="ficha__linha">
          Biogás teórico com{' '}
          <strong>{porcento(resposta.fracao_ch4_teorica * 100, 1)} de CH₄</strong>, e{' '}
          {enxuto(resposta.tbmp_por_g_carbono)} NmL CH₄ por grama de carbono.
        </p>
        <p className="terciario">
          Teórico quer dizer: o que a estequiometria permite se tudo degradar. O ensaio
          mede uma fração disso, e a distância entre os dois é a recalcitrância do material.
        </p>
      </section>

      <section className="bloco">
        <h3 className="rotulo-secao">Com o que esta razão é compatível</h3>
        {/* A lista vazia é um resultado, não uma falha. A família
            `carboidrato, proteína, acetato` cobre uma faixa inteira de DQO/COT
            sem que nenhum composto PURO caia exatamente nela — e uma tela que
            escondesse a seção quando a lista vem vazia deixaria o usuário sem
            saber se a API não achou nada ou se nem procurou. */}
        <p className="afirmacao">Família: {resposta.familia}.</p>
        {resposta.compativeis.length === 0 ? (
          <p className="terciario">
            Nenhum composto puro do catálogo cai exatamente nesta razão. Isso é o esperado
            para material real, que é mistura — a família acima é a leitura que cabe.
          </p>
        ) : (
          <div className="empilha">
            {resposta.compativeis.map((c) => (
              <div className="serie" key={c.chave}>
                <p className="serie__titulo">
                  {c.rotulo} <span className="terciario">{c.formula}</span>
                </p>
                <p className="serie__numeros">
                  <span>DQO/COT {enxuto(c.dqo_cot, 2)}</span>
                  {c.dbo_dqo_tipica ? (
                    <span className="terciario">
                      DBO/DQO típica {c.dbo_dqo_tipica[0]}–{c.dbo_dqo_tipica[1]}
                    </span>
                  ) : null}
                </p>
                {c.observacao ? <p className="campo__dica">{c.observacao}</p> : null}
              </div>
            ))}
          </div>
        )}
        {resposta.ambiguo ? (
          <p className="campo__dica campo__dica--atencao">
            Esta razão é ambígua: compostos de prognóstico oposto a compartilham. A régua
            sozinha não decide entre eles, e fingir que decide seria o pior serviço que
            esta tela poderia prestar.
          </p>
        ) : null}
      </section>

      <section className="bloco">
        <h3 className="rotulo-secao">E o material degrada?</h3>
        {/* O eixo da DBO fica em seção SEPARADA de propósito: juntá-lo ao da
            oxidação é exatamente o erro de leitura que a tela existe para
            evitar. Carbono reduzido e carbono acessível são coisas diferentes,
            e a lignina é o contraexemplo que prova isso. */}
        {resposta.veredito ? (
          <p className="afirmacao">{resposta.veredito}</p>
        ) : (
          <p className="afirmacao">
            Não dá para dizer. Sem DBO não há veredito de biodegradabilidade — e tudo
            acima continua valendo, porque é outro eixo.
          </p>
        )}
      </section>

      {resposta.avisos.length > 0 ? (
        <section className="bloco">
          <h3 className="rotulo-secao">Avisos</h3>
          <div className="empilha">
            {resposta.avisos.map((a) => (
              <div key={a.codigo} className={`serie ${a.grave ? 'serie--engana' : ''}`}>
                <p className="ficha__linha">{a.mensagem}</p>
                {a.fonte ? <p className="serie__fonte">{a.fonte}</p> : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}
